import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import { Subscription } from 'rxjs';
import { EmdChartBase } from '../emd-chart-base';
import { EmdTooltipRow } from '../emd-chart-tooltip.component';
import { EmdMapDataService, EmdMapReference } from '../emd-map-data.service';

export interface EmdMapDatum { wholesaler: string; location: string; sales: number; }

interface Bubble {
  sourceName: string;
  locationName: string;
  sales: number;
  sourceTotal: number;
  coordinates: [number, number];
  /** How many wholesaler bubbles share this exact location (>1 = a cluster). */
  cohort?: number;
  x?: number;
  y?: number;
}

/**
 * US wholesaler map: one bubble per wholesaler/location, force-separated so
 * overlapping cities stay readable, each printing its sales figure.
 *
 * The state boundaries and city coordinates come from a static asset via
 * EmdMapDataService — the chart itself takes everything else through inputs.
 */
@Component({
    selector: 'emd-bubble-map-chart',
    templateUrl: './emd-bubble-map-chart.component.html',
    styleUrls: ['./emd-bubble-map-chart.component.scss'],
    standalone: false
})
export class EmdBubbleMapChartComponent extends EmdChartBase {
  @Input() data: EmdMapDatum[] = [];
  @Input() colorMap: Record<string, string> = {};
  @Input() chartHeight = 500;

  @Output() bubbleClick = new EventEmitter<{ sourceName: string; ctrlKey: boolean }>();

  @ViewChild('chart', { static: true }) chartRef!: ElementRef<HTMLDivElement>;

  loading = true;
  tooltipVisible = false;
  tooltipTitle = '';
  tooltipRows: EmdTooltipRow[] = [];
  tooltipX = 0;
  tooltipY = 0;

  /** Locations with no coordinate match; surfaced on the aria-label, not on screen. */
  unmatched = 0;

  /** Live zoom readout for the control bar, as in the original's .zoomValueLabel. */
  zoomPercent = 100;

  private reference?: EmdMapReference;
  private sub?: Subscription;
  private bubbles: Bubble[] = [];
  private sourceOrder: string[] = [];
  private highlighted: string[] | null = null;
  private visibleSources: string[] | null = null;
  private focusIndex = -1;
  private zoomBehavior?: d3.ZoomBehavior<Element, unknown>;
  private simulation?: d3.Simulation<any, undefined>;
  /** Re-sizes/reveals bubble count labels for a zoom factor; set in render(). */
  private relabelForZoom?: (k: number) => void;

  constructor(host: ElementRef<HTMLElement>, private readonly mapData: EmdMapDataService) {
    super(host);
  }

  override ngAfterViewInit(): void {
    this.sub = this.mapData.load().subscribe({
      next: (reference) => {
        this.reference = reference;
        this.loading = false;
        super.ngAfterViewInit();
      },
      error: () => {
        this.loading = false;
        super.ngAfterViewInit();
      },
    });
  }

  override ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.simulation?.stop();
    super.ngOnDestroy();
  }

  get hasData(): boolean {
    return this.data.length > 0;
  }

  get ariaLabel(): string {
    if (!this.bubbles.length) { return 'Wholesaler location map, no data'; }
    const sources = new Set(this.bubbles.map((b) => b.sourceName)).size;
    const note = this.unmatched > 0 ? ` ${this.unmatched} location(s) could not be mapped.` : '';
    return `Map of ${this.bubbles.length} wholesaler locations across ${sources} wholesalers.${note} Use arrow keys to move between locations.`;
  }

  /** Hover preview — one source emphasised, the rest dimmed. */
  highlightSource(name: string | null): void {
    this.highlightSources(name ? [name] : null);
  }

  /** Hover preview for a set of sources. */
  highlightSources(names: string[] | null): void {
    this.highlighted = names && names.length ? names : null;
    this.applyHighlight();
  }

  /** Pinned legend selection — everything not named is removed from the map. */
  setVisibleSources(names: string[] | null): void {
    this.visibleSources = names && names.length ? names : null;
    this.applyHighlight();
  }

  private applyHighlight(): void {
    const active = this.highlighted;
    const visible = this.visibleSources;
    const groups = d3
      .select(this.chartRef?.nativeElement)
      .selectAll<SVGGElement, Bubble>('g.emd-bubble-group');

    // Instant, as in the original: legend hover snapped rather than eased.
    groups.attr('display', (d) =>
      !visible || visible.indexOf(d.sourceName) > -1 ? 'block' : 'none'
    );
    groups.attr('opacity', (d) =>
      !active || active.indexOf(d.sourceName) > -1 ? 1 : 0.2
    );
  }

  private colorFor(sourceName: string): string {
    const mapped = this.colorMap[sourceName];
    if (mapped) { return mapped; }
    const i = this.sourceOrder.indexOf(sourceName);
    const palette = EmdBubbleMapChartComponent.FALLBACK_COLORS;
    return palette[(i < 0 ? 0 : i) % palette.length];
  }

  /** Translucent bubble fill so overlaps stay legible; the border stays opaque. */
  private static readonly BUBBLE_FILL_OPACITY = 0.62;

  /** The original chart's 28-colour palette, copied by value (no shared import). */
  private static readonly FALLBACK_COLORS = [
    '#8C23E6', '#E223E6', '#FFCD4A', '#645DD7', 'gold', 'gray', 'purple',
    'orange', '#E74028', '#2CB239', '#25D3EB', '#FFB039', '#1566DE', '#7848FF',
    '#7AD045', '#CB04DC', '#FA195C', '#9E0000', '#EACD37', '#DD630B', '#BCD03F',
    '#14BDA9', '#8A05F3', '#D9476A', '#2DADB6', '#E468C2', '#199CD4', '#0934CC',
  ];

  protected render(): void {
    const el = this.chartRef?.nativeElement;
    if (!el || !this.reference || !this.width) { return; }

    d3.select(el).selectAll('svg').remove();

    this.bubbles = this.buildBubbles();
    if (!this.bubbles.length) { return; }

    const height = this.effectiveHeight(this.chartHeight);
    const width = this.width;

    // Reserve the 36px control-bar strip plus the original's 40px pad, so the
    // US is fitted into the same box the original used (500 - 36 - 40 = 424).
    const mapHeight = height - 36 - 40;

    const svg = d3
      .select(el)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('role', 'presentation');

    const states = this.reference.statesTopojson;
    const feature: any = topojson.feature(states, states.objects.states_20m_2017);

    const projection: any = d3.geoAlbersUsa().fitSize([width - 40, mapHeight], feature);
    const path: any = d3.geoPath().projection(projection);

    // Albers USA drops Puerto Rico, so it gets its own projection and the
    // bubbles that land there fall back to its centroid.
    const prProjection = d3
      .geoConicEqualArea()
      .rotate([66, 0])
      .center([0, 18])
      .parallels([8, 18])
      .fitSize([width - 40, mapHeight], feature);
    const prPath: any = d3.geoPath().projection(prProjection);
    const prFeature = feature.features.find((f: any) => f.properties.NAME === 'Puerto Rico');
    const prCentroid: [number, number] = prFeature ? prPath.centroid(prFeature) : [0, 0];

    // Square clip 36px short of the panel bottom, so panned geometry never runs
    // under the zoom bar.
    const defs = svg.append('defs');
    defs
      .append('clipPath')
      .attr('id', `${this.uid}-map-clip`)
      .append('rect')
      .attr('width', width)
      .attr('height', mapHeight + 40);

    const clipGroup = svg.append('g').attr('clip-path', `url(#${this.uid}-map-clip)`);
    const zoomLayer = clipGroup.append('g').attr('class', 'emd-map-zoom').attr('stroke-width', 1);

    // ── States ────────────────────────────────────────────────────────────────
    const statesLayer = zoomLayer.append('g').attr('class', 'emd-map-states');

    // Puerto Rico is unbordered, matching the original's dedicated path.
    const stateStroke = (d: any) => (d.properties.NAME === 'Puerto Rico' ? 'none' : '#D9DDE4');

    const stateGroups = statesLayer
      .selectAll('g.emd-map-state')
      .data(feature.features)
      .enter()
      .append('g')
      .attr('class', 'emd-map-state');

    stateGroups
      .append('path')
      .attr('class', 'emd-state-path')
      .attr('d', (d: any) => (d.properties.NAME === 'Puerto Rico' ? prPath(d) : path(d)))
      .attr('fill', 'white')
      .attr('stroke', stateStroke)
      .on('mouseover', (event: MouseEvent) => {
        const node = event.currentTarget as SVGPathElement;
        d3.select(node.parentNode as any).raise();
        d3.select(node).attr('stroke', '#1363DF');
      })
      .on('mouseout', () => {
        statesLayer.selectAll('.emd-state-path').attr('stroke', stateStroke as any);
      });

    stateGroups
      .append('text')
      .attr('class', 'emd-state-label')
      .attr('x', (d: any) =>
        d.properties.NAME === 'Puerto Rico'
          ? prCentroid[0]
          : path.centroid(d)[0] + (d.properties.STUSPS === 'FL' ? 10 : 0)
      )
      .attr('y', (d: any) =>
        d.properties.NAME === 'Puerto Rico' ? prCentroid[1] : path.centroid(d)[1]
      )
      .attr('text-anchor', 'middle')
      .attr('pointer-events', 'none')
      .attr('font-size', 10)
      .attr('font-weight', '300')
      .attr('fill', '#8A98AB')
      .text((d: any) => d.properties.STUSPS);

    const project = (b: Bubble): [number, number] => {
      const point = projection(b.coordinates);
      return point ?? prCentroid;
    };

    // ── Bubbles ─────────────────────────────────────────────────────────────
    // Responsive size band: bubbles scale with the map's own width, so they are
    // small on a phone and grow proportionally on wide/fullscreen layouts. The
    // whole bubble layer also lives in the zoom group, so zooming scales the
    // band again and coincident clusters spread apart as you zoom in.
    const sizeK = Math.max(0.5, Math.min(1.25, width / 820));
    const rMin = 9 * sizeK;
    const rMax = 22 * sizeK;

    const extent = d3.extent(this.bubbles, (d) => d.sales) as [number, number];
    const thresholds = d3.scaleLinear().domain(extent).ticks(6);
    const range: number[] = [];
    const step = (rMax - rMin) / Math.max(thresholds.length, 1);
    for (let i = 0; i <= thresholds.length; i++) { range.push(rMin + step * i); }
    const radius = d3.scaleThreshold<number, number>().domain(thresholds).range(range);

    // Bubbles sharing an exact location form a cluster; the count drives the
    // tooltip note. (Jitter + collision below keep each one individually
    // visible rather than stacked.)
    const cityCount = d3.rollup(this.bubbles, (v) => v.length, (b) => b.locationName);
    this.bubbles.forEach((b) => { b.cohort = cityCount.get(b.locationName) ?? 1; });

    const bubbleGroup = zoomLayer.append('g').attr('class', 'emd-map-bubbles');

    const groups = bubbleGroup
      .selectAll('g.emd-bubble-group')
      .data(this.bubbles)
      .enter()
      .append('g')
      .attr('class', 'emd-bubble-group')
      .attr('transform', (d) => `translate(${project(d)[0]},${project(d)[1]})`);

    const nodes = groups
      .append('circle')
      .attr('class', 'emd-bubble')
      .attr('r', (d) => radius(d.sales))
      .attr('fill', (d) => this.colorFor(d.sourceName))
      // Translucent fill so overlapping regions stay legible; the stroke has no
      // opacity of its own, so the same-colour ring stays a crisp, visible
      // border on top of the see-through fill.
      .attr('fill-opacity', EmdBubbleMapChartComponent.BUBBLE_FILL_OPACITY)
      .attr('stroke', (d) => this.colorFor(d.sourceName))
      .attr('cursor', 'default');

    groups
      .append('text')
      .attr('class', 'emd-bubble-label')
      .attr('text-anchor', 'middle')
      .attr('pointer-events', 'none')
      .attr('font-weight', '500')
      .attr('fill', 'white')
      .text((d) => d3.format(',.0f')(d.sales));

    // Show/size the in-bubble count for the current zoom. Labels live in the
    // zoom layer, so both bubble and text scale together — a count that doesn't
    // fit at 100% would never fit at any zoom. Counter-scaling the font (keeping
    // it a constant on-screen size) means that as the user zooms in, small
    // bubbles grow on screen and reveal their count once it fits.
    this.relabelForZoom = (k: number) => {
      groups.select<SVGTextElement>('text.emd-bubble-label').each((d: any, i, nodes) => {
        const text = d3.format(',.0f')(d.sales);
        const screenDiameter = radius(d.sales) * 2 * k;
        let font = 13;
        while (font > 8 && this.measureWidth(text, font) + 4 > screenDiameter) { font -= 1; }
        const fits = this.measureWidth(text, font) + 4 <= screenDiameter;
        d3.select(nodes[i] as SVGTextElement)
          .attr('display', fits ? null : 'none')
          .attr('font-size', font / k)
          .attr('y', (font * 0.4) / k);
      });
    };
    this.relabelForZoom(1);

    // Press point for the mousedown/mouseup click detection below.
    let pressX = 0;
    let pressY = 0;

    nodes
      .on('mouseenter', (event: MouseEvent, d: Bubble) => {
        const circle = event.currentTarget as SVGCircleElement;
        d3.select(circle.parentNode as any).raise();
        // Solidify the hovered bubble so it reads clearly out of an overlap.
        d3.select(circle).attr('stroke', '#1363DF').attr('fill-opacity', 0.92);
        // Bring the whole wholesaler group forward, so buried bubbles surface.
        bubbleGroup
          .selectAll<SVGGElement, Bubble>('g.emd-bubble-group')
          .filter((g) => g.sourceName === d.sourceName)
          .raise();
        this.openTooltip(event.clientX, event.clientY, d);
      })
      .on('mouseleave', () => {
        bubbleGroup
          .selectAll<SVGCircleElement, Bubble>('circle.emd-bubble')
          .attr('stroke', (d) => this.colorFor(d.sourceName))
          .attr('fill-opacity', EmdBubbleMapChartComponent.BUBBLE_FILL_OPACITY);
        this.tooltipVisible = false;
      })
      // The svg carries d3-zoom, which suppresses the synthetic `click` on its
      // children — so a plain bubble click never reached a `.on('click')`
      // handler. Detect it ourselves: a mousedown then a mouseup on the same
      // bubble with < 10px of drift is a click (more than that is a pan).
      .on('mousedown', (event: MouseEvent) => {
        pressX = event.clientX;
        pressY = event.clientY;
      })
      .on('mouseup', (event: MouseEvent, d: Bubble) => {
        if (Math.hypot(event.clientX - pressX, event.clientY - pressY) <= 10) {
          this.bubbleClick.emit({
            sourceName: d.sourceName,
            ctrlKey: event.ctrlKey || event.metaKey,
          });
        }
      });

    // Seed with a deterministic golden-angle offset so bubbles sharing an exact
    // city do not start perfectly coincident — collision needs a gradient to
    // push them apart, otherwise stacked points can sit on top of each other.
    this.bubbles.forEach((b, i) => {
      const [px, py] = project(b);
      const a = i * 2.399963; // golden angle (rad)
      b.x = px + Math.cos(a) * 0.75;
      b.y = py + Math.sin(a) * 0.75;
    });

    // Stop any prior simulation so re-renders (resize, data change, tab switch)
    // do not leave ticking sims competing for the main thread.
    this.simulation?.stop();
    this.simulation = d3
      .forceSimulation(this.bubbles as any)
      // Absolute 1.5px gap (not just a %) so even small bubbles keep a visible
      // border-to-border gap; stronger + iterated so nothing fully overlaps.
      .force('collision', d3.forceCollide((d: any) => radius(d.sales) + 1.5).strength(0.85).iterations(2))
      // Anchored firmly to the true city so clusters spread locally without
      // drifting away from where they actually belong on the map.
      .force('x', d3.forceX((d: any) => project(d)[0]).strength(0.3))
      .force('y', d3.forceY((d: any) => project(d)[1]).strength(0.3))
      .on('tick', () => groups.attr('transform', (d: any) => `translate(${d.x},${d.y})`));

    this.zoomBehavior = d3
      .zoom<any, unknown>()
      .extent([[0, 0], [width, mapHeight]])
      .scaleExtent([1, 8])
      .translateExtent([[0, 0], [width, mapHeight]])
      // d3-zoom defaults clickDistance to 0, so ANY pointer drift between
      // mousedown and mouseup on the svg suppresses the following click — which
      // silently killed bubble cross-filter clicks. Allow up to 10px of drift.
      .clickDistance(10)
      .on('zoom', (event: any) => {
        zoomLayer.attr('transform', event.transform);
        // Counter-scale so state and bubble outlines stay a hairline at any zoom.
        zoomLayer.attr('stroke-width', 1 / event.transform.k);
        this.zoomPercent = Math.trunc(event.transform.k * 100);
        // Reveal/resize the in-bubble counts as the zoom grows small bubbles.
        this.relabelForZoom?.(event.transform.k);
        // Zooming slides bubbles out from under a stationary pointer, so the
        // bubble's own mouseleave never fires; hide here instead.
        this.tooltipVisible = false;
      });

    // Default dblclick.zoom left in place: double-click zooms in, as before.
    svg.call(this.zoomBehavior as any);

    // Bubble-level mouseleave can be missed when raise()/zoom re-shuffle the
    // DOM under the cursor — leaving the map always dismisses the tooltip.
    svg.on('mouseleave', () => {
      this.tooltipVisible = false;
    });

    this.applyHighlight();
  }

  private buildBubbles(): Bubble[] {
    const cities = this.reference?.cityCoords ?? [];
    const index = new Map<string, { lat: number; lng: number }>();
    cities.forEach((c) =>
      index.set(`${String(c.city).toUpperCase()}, ${c.state_id}`, {
        lat: +c.lat,
        lng: +c.lng,
      })
    );

    const bubbles: Bubble[] = [];
    let unmatched = 0;

    const bySource = d3.group(this.data, (d) => d.wholesaler);
    this.sourceOrder = Array.from(bySource.keys());

    bySource.forEach((rows, sourceName) => {
      const sourceTotal = d3.sum(rows, (r) => r.sales);
      const byLocation = d3.rollup(
        rows,
        (r) => d3.sum(r, (s) => s.sales),
        (g) => g.location
      );

      byLocation.forEach((sales, locationName) => {
        const match = index.get(String(locationName).toUpperCase());
        if (!match) { unmatched++; return; }
        bubbles.push({
          sourceName,
          locationName,
          sales,
          sourceTotal,
          coordinates: [match.lng, match.lat],
        });
      });
    });

    this.unmatched = unmatched;
    // Left in wholesaler-group order, as the original was: each source's
    // bubbles stay contiguous and later sources paint over earlier ones.
    return bubbles;
  }

  private measureWidth(text: string, fontSize: number): number {
    const context = document.createElement('canvas').getContext('2d');
    if (!context) { return 0; }
    context.font = `${fontSize}px sans-serif`;
    return context.measureText(text).width;
  }

  // ── Zoom controls ───────────────────────────────────────────────────────────

  zoomIn(): void { this.scaleZoom(1.1); }
  zoomOut(): void { this.scaleZoom(0.9); }

  resetZoom(): void {
    if (!this.zoomBehavior) { return; }
    // Instant, as in the original: no transition on reset.
    d3.select(this.chartRef.nativeElement)
      .select('svg')
      .call(this.zoomBehavior.transform as any, d3.zoomIdentity);
    this.zoomPercent = 100;
  }

  private scaleZoom(factor: number): void {
    if (!this.zoomBehavior) { return; }
    // Instant, as in the original: zoom.scaleBy with no transition.
    d3.select(this.chartRef.nativeElement)
      .select('svg')
      .call(this.zoomBehavior.scaleBy as any, factor);
  }

  // ── Tooltip ─────────────────────────────────────────────────────────────────

  private openTooltip(clientX: number, clientY: number, b: Bubble): void {
    this.tooltipTitle = `Quantity Shipped (${b.locationName})`;
    // Value carried in `label` so it sits next to the colour dot, as the
    // original did — city sales over the wholesaler's total.
    this.tooltipRows = [
      {
        label: `${b.sales.toLocaleString('en-US')}/${b.sourceTotal.toLocaleString('en-US')}`,
        value: '',
        color: this.colorFor(b.sourceName),
      },
    ];
    // When several wholesalers ship to this same city, note the cluster size so
    // the overlap the user is looking at is explained (they can zoom to fan it out).
    if (b.cohort && b.cohort > 1) {
      this.tooltipRows.push({ label: `${b.cohort} wholesalers at this location`, value: '' });
    }
    this.tooltipVisible = true;
    this.positionTooltip(clientX, clientY);
  }

  private positionTooltip(clientX: number, clientY: number): void {
    const rect = this.host.nativeElement.getBoundingClientRect();
    this.tooltipX = clientX - rect.left;
    this.tooltipY = clientY - rect.top;
  }

  // ── Keyboard ────────────────────────────────────────────────────────────────

  onKeydown(event: KeyboardEvent): void {
    if (!this.bubbles.length) { return; }

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        this.focusIndex = (this.focusIndex + 1) % this.bubbles.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        this.focusIndex = (this.focusIndex - 1 + this.bubbles.length) % this.bubbles.length;
        break;
      case 'Enter':
      case ' ':
        if (this.focusIndex >= 0) {
          event.preventDefault();
          this.bubbleClick.emit({
            sourceName: this.bubbles[this.focusIndex].sourceName,
            ctrlKey: event.ctrlKey || event.metaKey,
          });
        }
        return;
      case 'Escape':
        this.focusIndex = -1;
        this.tooltipVisible = false;
        d3.select(this.chartRef.nativeElement).selectAll('.emd-bubble-focus').remove();
        return;
      default:
        return;
    }

    event.preventDefault();
    this.focusBubble(this.bubbles[this.focusIndex]);
  }

  /** Ring the focused bubble and open its tooltip, so keyboard reaches it too. */
  private focusBubble(b: Bubble): void {
    const svg = d3.select(this.chartRef.nativeElement).select('svg');
    svg.selectAll('.emd-bubble-focus').remove();

    const node = svg
      .selectAll<SVGCircleElement, Bubble>('circle.emd-bubble')
      .filter((d) => d === b);
    const element = node.node();
    if (!element) { return; }

    const r = +element.getAttribute('r')!;

    // The ring is appended to the bubble's own translated group, so it is
    // positioned at the group origin rather than in map coordinates.
    d3.select(element.parentNode as any)
      .append('circle')
      .attr('class', 'emd-bubble-focus')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', r + 4)
      .attr('fill', 'none')
      .attr('stroke', '#101d42')
      .attr('stroke-width', 2)
      .attr('pointer-events', 'none');

    const rect = element.getBoundingClientRect();
    this.openTooltip(rect.left + rect.width / 2, rect.top, b);
  }

  onBlur(): void {
    this.focusIndex = -1;
    this.tooltipVisible = false;
    d3.select(this.chartRef?.nativeElement).selectAll('.emd-bubble-focus').remove();
  }
}
